import { rux } from './../../../lib/rux.js'
// Namespaced store that has all the CRUD updates for Packing List Templates.
const PackingListStore = {
  namespaced: true,
  state: () => ({
    list: [],
    current: {
      dbKey: null,
      packingListItems: [],
      images: [],
      shareableLink: '',
      description: ''
    }
  }),
  actions: {
    // Add a template. This uses the DBkey for the rest of the CRUD options. it uses the name as the slug
    async add ({ dispatch, commit }, value) {
      const { name } = value
      try {
        // Add to the database
        const dbKey = await rux.addPackingListTemplate({ name })
        // Create a link that allows public viewing of the template.
        const shareableLink = createShareableLink(dbKey)
        // Add to the Vue store
        commit('SETCURRENTPACKINGLISTTEMPLATE', { name, dbKey, shareableLink })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    // Deletes a template
    async delete ({ dispatch, commit, state }, params) {
      const { item } = params
      try {
        // Delete from the DB
        await rux.deletePackingListTemplate({ dbKey: item.dbKey })
        // Delete from the UI
        commit('DELETEPACKINGLISTTEMPLATE', { dbKey: item.dbKey })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    // Updates the store and db when something is changed.
    async update ({ dispatch, commit, state }, value) {
      const { name, description, isPublic } = value
      // Can we safely remove this?
      const shareableLink = createShareableLink(state.current.dbKey)
      try {
        // Update the packing list.
        await rux.updatePackingListTemplate({
          name: name || state.current.name, // Update the name if its present, or leave as current
          // This one is a little tricky. Make sure that if they clear the description it allows '' to be saved and return null if there is actually nothing ever
          description: allowEmptyString(description, state.current.description),
          key: state.current.dbKey,
          isPublic: allowFalseBool(isPublic, state.current.isPublic)
        })
        // Add to the UI
        commit('SETCURRENTPACKINGLISTTEMPLATE', {
          name: name || state.current.name,
          dbKey: state.current.dbKey,
          shareableLink,
          description: allowEmptyString(description, state.current.description),
          packingListItems: state.current.packingListItems,
          isPublic: allowFalseBool(isPublic, state.current.isPublic)
        })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    // Adds a Packing List item the current template
    async addPackingListItemToTemplate ({ dispatch, commit, state }, value) {
      const { item } = value
      try {
        // Add it to the DB
        const checkedKey = await rux.addPackingListItemToTemplate({ item, key: state.current.dbKey })
        // Add it to the UI
        commit('ADDPACKINGLISTITEMTOCURRENTTEMPLATE', { value: item, checked: false, key: checkedKey })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    // Deletes a packing list item from the current template
    async deletePackingListItemFromTemplate ({ dispatch, commit, state }, value) {
      const { item, index } = value
      try {
        // Delete from the db
        await rux.deletePackingListItemFromTemplate({ item, key: state.current.dbKey })
        // Delete from the UI
        commit('DELETEPACKINGLISTITEMFROMCURRENTTEMPLATE', index)
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    // Gets all the templates
    async getAllPackingListTemplates ({ dispatch, commit }) {
      try {
        // Get all of the templates and all of the images as well.
        const templates = await rux.getPackingListTemplates()
        const promises = templates.map((template) => {
          return rux.getPackingListTemplateImages(template.dbKey)
        })
        // wait for all the promises to resolve before pushing it to the UI
        Promise.all(promises).then((result) => {
          result.forEach((res, index) => {
            templates[index].images = res
          })
          commit('SETPACKINGLISTTEMPLATES', templates)
        })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    // Gets an individual template item.
    async getPackingListTemplateItem ({ dispatch, commit }, val) {
      // Show the loading screen for this one.
      dispatch('setShowLoading', true, { root: true })
      const { name } = val
      try {
        // Get from the db.
        const template = await rux.getPackingListTemplateItem(name)
        template.shareableLink = createShareableLink(template.dbKey)
        commit('SETCURRENTPACKINGLISTTEMPLATE', template)
        dispatch('setShowLoading', false, { root: true })
      } catch (error) {
        dispatch('setShowLoading', false, { root: true })
        dispatch('error', error, { root: true })
      }
    },
    // Clears the current packing list template from the store. Doesn't touch the DB
    async clearCurrentPackingListTemplate ({ commit }) {
      commit('CLEARCURRENTPACKINGLISTTEMPLATE')
    },
    // Gets the public packing list template. Requires the UID to be passed thru which is part of the link that is generated for sharing
    async getPublicPackingListTemplate ({ commit, dispatch }, val) {
      const { uid, dbKey } = val
      try {
        // Get the iamges and template from the DB.
        const template = await rux.getPublicPackingListTemplateItem(uid, dbKey)
        const images = await rux.getPublicPackingListTemplateImages(uid, dbKey)
        if (template[0].isPublic) {
        // add it to the store
          commit('SETCURRENTPACKINGLISTTEMPLATE', template[0])
          commit('SETIMAGESFORCURRENTPACKINGLISTTEMPLATE', images)
        } else {
          dispatch('error', { message: 'That Packing List is currently private.' }, { root: true })
        }
      } catch (error) {
        dispatch('error', { message: 'Unable to find that Packing List. Perhaps it has been deleted' }, { root: true })
      }
    },
    // Adds an image to the current template
    async addImage ({ commit, dispatch, state }, value) {
      const { image, replace } = value
      try {
        // Add to the db and get the URL back.
        const imageRef = await rux.addPackingListTemplateImage({ image, dbKey: state.current.dbKey })
        const imagePath = await imageRef.ref.getDownloadURL()
        // Add to the UI
        commit('ADDIMAGETOPACKINGLISTTEMPLATE', { imagePath, imageName: image.name, replace })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    // Deletes an Image
    async deleteImage ({ commit, dispatch, state }, value) {
      try {
        // Delete from the DB
        await rux.deletePackingListTemplateImage({ ...value, dbKey: state.current.dbKey })
        // Delete from the UI
        commit('DELETEPACKINGLISTTEMPLATEIMAGE', { index: value.index })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    // Gets the images for a template from the firebase Storage.
    async getImages ({ commit, dispatch, state }) {
      try {
        const images = await rux.getPackingListTemplateImages(state.current.dbKey)
        commit('SETIMAGESFORCURRENTPACKINGLISTTEMPLATE', images)
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    }
  },
  mutations: {
    SETCURRENTPACKINGLISTTEMPLATE (state, val) {
      const { dbKey, name, shareableLink, description, packingListItems, createdBy, isPublic } = val
      state.current = {
        ...state.current,
        dbKey,
        name,
        shareableLink,
        description,
        packingListItems,
        createdBy,
        isPublic
      }
    },
    DELETEPACKINGLISTTEMPLATE (state, val) {
      const index = state.list.findIndex((item) => {
        return item.dbKey === val.dbKey
      })
      state.list.splice(index, 1)
    },
    ADDPACKINGLISTITEMTOCURRENTTEMPLATE (state, val) {
      state.current.packingListItems.push(val)
    },
    DELETEPACKINGLISTITEMFROMCURRENTTEMPLATE (state, val) {
      state.current.packingListItems.splice(val, 1)
    },
    SETPACKINGLISTTEMPLATES (state, val) {
      state.list = val
    },
    CLEARCURRENTPACKINGLISTTEMPLATE (state) {
      state.current = {
        packingListItems: [],
        dbKey: null,
        images: []
      }
    },
    // Adds an image to the specified template. Need to move this logic out of the mutation and into the action.
    ADDIMAGETOPACKINGLISTTEMPLATE (state, val) {
      // Unpack the values
      const { imageName, imagePath, replace } = val
      // If the client decides to replace, find it in the list and replace.
      if (replace) {
        const matchedImageIndex = state.current.images.findIndex((img) => {
          return imageName === img.match('%2..*%2F(.*?)?alt')[1].slice(0, -1)
        })
        if (matchedImageIndex) {
          state.current.images.splice(matchedImageIndex, 1)
        }
      }
      // Other wise just add it on the end
      state.current.images.push(imagePath)
    },
    SETIMAGESFORCURRENTPACKINGLISTTEMPLATE (state, val) {
      state.current.images = val
    },
    DELETEPACKINGLISTTEMPLATEIMAGE (state, val) {
      const { index } = val
      state.current.images.splice(index, 1)
    }
  }
}
// Creates a link that is displayed so end users can share the template
const createShareableLink = (dbKey) => {
  return window.location.origin + '/packing-list/public/' + rux.getAuthUID() + '/' + dbKey
}
// Allows an empty string to be saved, or null if its actually nothing
const allowEmptyString = (string, stateString) => {
  if (!string && !stateString) return null
  if (string === '') return string
  else return string || stateString
}
const allowFalseBool = (bool, stateBool) => {
  if (bool === undefined && stateBool === undefined) return null
  if (bool === false) return bool
  else return bool || stateBool
}
export { PackingListStore }
